import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ViewMode } from "containers/project-boards/types";
import { SearchIcon, GridIcon, ListIcon } from "./icons";

interface BoardsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onCreate: () => void;
  createLabel: string;
  searchPlaceholder: string;
}

export default function BoardsToolbar({
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onCreate,
  createLabel,
  searchPlaceholder,
}: BoardsToolbarProps) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const nextMode: ViewMode = viewMode === "grid" ? "list" : "grid";

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[200px] flex-1">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--p-color-text-secondary)]">
          <SearchIcon />
        </span>
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] py-2 pl-10 pr-3 text-base text-[var(--p-color-text)] lg:text-sm placeholder:text-[var(--p-color-text-secondary)] focus:border-[var(--p-color-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--p-color-border-focus)]"
        />
      </div>

      {/* Single toggle: shows the view you'll switch TO. */}
      <button
        type="button"
        onClick={() => onViewModeChange(nextMode)}
        aria-label={
          viewMode === "grid" ? "Switch to list view" : "Switch to grid view"
        }
        title={viewMode === "grid" ? "List view" : "Grid view"}
        className="rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] p-2 max-md:p-3 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)] hover:text-[var(--p-color-text)]"
      >
        {viewMode === "grid" ? <ListIcon /> : <GridIcon />}
      </button>

      {companyId && (
        <Link
          to={`/app/${companyId}/project-boards/opportunity-history`}
          className="shrink-0 rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-4 py-2 max-md:flex max-md:min-h-11 max-md:items-center text-sm font-semibold text-[var(--p-color-text)] hover:bg-[var(--p-color-bg-surface-secondary)]"
        >
          {t("project_opportunity_history_short", {
            defaultValue: "Opportunities",
          })}
        </Link>
      )}

      <button
        type="button"
        onClick={onCreate}
        className="shrink-0 rounded-lg bg-[var(--p-color-bg-fill-brand)] px-4 py-2 max-md:min-h-11 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
      >
        {createLabel}
      </button>
    </div>
  );
}
